import React, { useMemo } from "react";
import { Row, Form, Col } from "react-bootstrap";
import { useForm } from "react-hook-form";
import { filter } from "underscore";
import variables from "../styles/utils.module.scss";
import { Badge } from "react-bootstrap";
import { ArrowRightIcon } from "lucide-react";
import useSWR from "swr";
import { fetcher, serverUrl, swrConfig } from "../helpers/utility";

const S3_BUCKET_URL = "https://bplacedprivate.s3.eu-west-2.amazonaws.com";

function ServiceCard({ service }) {
  // Format location string from location object
  const locationString = service.location?.townCity
    ? `${service.location.townCity}${service.location.postcode ? `, ${service.location.postcode}` : ""}`
    : null;

  // Format delivery method display
  const deliveryDisplay =
    service.deliveryMethod === "in-person"
      ? "In-Person"
      : service.deliveryMethod === "online"
      ? "Online"
      : "In-Person & Online";

  // Get image URL if available
  const imageUrl = service.imageUrl?.externalId
    ? `${S3_BUCKET_URL}/${service.imageUrl.externalId}`
    : null;

  return (
    <div
      style={{
        display: "flex",
        flexDirection: "row",
        flexGrow: 1,
        backgroundColor: variables.pureWhite,
        borderRadius: "6px",
        padding: "0",
        border: "1px solid #e7eeee",
        maxHeight: "225px",
        position: "relative",
      }}
    >
      {/* IMAGE/LOGO COLUMN */}
      <div
        style={{
          aspectRatio: "1/1",
          width: "30%",
          marginRight: 0,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          backgroundColor: variables.offWhite,
          borderRadius: "6px 0 0 6px",
          overflow: "hidden",
        }}
      >
        {imageUrl ? (
          <img
            src={imageUrl}
            alt={service.businessName}
            style={{
              width: "100%",
              height: "100%",
              objectFit: "cover",
            }}
          />
        ) : (
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              color: variables.grey,
              fontSize: 12,
              textAlign: "center",
              flexDirection: "column",
              gap: 4,
            }}
          >
            <i className="bi bi-image" style={{ fontSize: 24 }}></i>
            <span>No image</span>
          </div>
        )}
      </div>

      {/* CONTENT COLUMN */}
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          width: "70%",
          padding: "15px",
          flexGrow: 1,
          minWidth: 0,
        }}
      >
        {/* HEADER WITH TITLE AND BADGES */}
        <div
          style={{
            display: "flex",
            flexDirection: "row",
            justifyContent: "space-between",
            alignItems: "flex-start",
            marginBottom: 10,
          }}
        >
          <div style={{ flex: 1, marginRight: 10 }}>
            <h1 className="h5" style={{ fontWeight: "bold", marginBottom: 4 }}>
              {service.businessName}
            </h1>
            <div style={{ fontSize: 14, color: variables.darkGrey }}>{deliveryDisplay}</div>
          </div>
          <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-end", gap: 6 }}>
            {locationString && <Badge bg="secondary">{locationString}</Badge>}
            {service.serviceType && <Badge bg="primary">{service.serviceType}</Badge>}
            {service.targetAgeGroup?.length > 0 && (
              <Badge className="text-white badge bg-secondary">
                Ages: {service.targetAgeGroup.join(", ")}
              </Badge>
            )}
          </div>
        </div>

        <div
          style={{
            display: "flex",
            flexDirection: "column",
            flexGrow: 1,
            maxHeight: "175px",
            overflowY: "auto",
          }}
        >
          {service.shortDescription && (
            <div
              style={{ fontSize: 13, color: variables.darkGrey, marginBottom: 12, lineHeight: 1.5 }}
            >
              {service.shortDescription}
            </div>
          )}
        </div>

        <div
          style={{
            display: "flex",
            flexDirection: "row",
            justifyContent: "flex-end",
            alignItems: "center",
            marginTop: "auto",
            paddingTop: 10,
            borderTop: "1px solid #e7eeee",
          }}
        >
          {service.websiteLink ? (
            <a
              href={service.websiteLink}
              target="_blank"
              rel="noopener noreferrer"
              className="ms-auto text-primary"
              style={{ textDecoration: "none", display: "flex", alignItems: "center", gap: 4 }}
            >
              Visit Website <ArrowRightIcon size={16} />
            </a>
          ) : service.contactEmail ? (
            <a
              href={`mailto:${service.contactEmail}`}
              className="ms-auto text-primary"
              style={{ textDecoration: "none", display: "flex", alignItems: "center", gap: 4 }}
            >
              Contact <ArrowRightIcon size={16} />
            </a>
          ) : (
            <span className="ms-auto text-muted" style={{ fontSize: 13 }}>
              No contact info
            </span>
          )}
        </div>
      </div>
    </div>
  );
}

export default function ServicesContent() {
  // Fetch services from the database
  const { data: serviceData, isLoading } = useSWR(`${serverUrl}/service`, fetcher, swrConfig);

  const {
    register,
    watch,
  } = useForm({
    defaultValues: {
      serviceName: "",
      deliveryMethod: "all",
      serviceType: "all",
    },
  });
  const formValues = watch();

  // Filter services based on form values
  const filteredServices = useMemo(() => {
    if (!serviceData?.services) return [];

    let filtered = [...serviceData.services];

    // Filter by service name (business name)
    if (formValues.serviceName && formValues.serviceName.length > 0) {
      filtered = filter(filtered, (service) =>
        service.businessName.toLowerCase().includes(formValues.serviceName.toLowerCase())
      );
    }

    // Filter by delivery method
    if (formValues.deliveryMethod && formValues.deliveryMethod !== "all") {
      filtered = filter(filtered, (service) => service.deliveryMethod === formValues.deliveryMethod);
    }

    // Filter by service type
    if (formValues.serviceType && formValues.serviceType !== "all") {
      filtered = filter(filtered, (service) => service.serviceType === formValues.serviceType);
    }

    return filtered;
  }, [formValues, serviceData]);

  if (isLoading) {
    return (
      <Row>
        <div style={{ display: "flex", flexDirection: "column", padding: 15 }}>
          <div className="text-center py-5">
            <div className="spinner-border text-primary" role="status">
              <span className="visually-hidden">Loading...</span>
            </div>
          </div>
        </div>
      </Row>
    );
  }

  return (
    <Row>
      <div style={{ display: "flex", flexDirection: "column", padding: 15 }}>
        {/* HEADER */}
        <div style={{ marginBottom: 20 }}>
          <h1 className="h3" style={{ marginBottom: 5 }}>
            Services
          </h1>
          <div className="text-muted medium pb-3">
            {filteredServices.length} {filteredServices.length === 1 ? "service" : "services"} found
          </div>

          {/* SEARCH */}
          <div
            style={{
              backgroundColor: variables.pureWhite,
              padding: 15,
              borderRadius: "6px",
              border: `1px solid ${variables.lightGrey}`,
            }}
          >
            <Row className="g-3">
              <Col md={6} lg={4}>
                <Form.Label style={{ fontSize: 14, marginBottom: 5 }}>Service Name</Form.Label>
                <Form.Control
                  type="text"
                  placeholder="Search by service name"
                  {...register("serviceName")}
                  id="serviceName"
                />
              </Col>
              <Col md={6} lg={4}>
                <Form.Label style={{ fontSize: 14, marginBottom: 5 }}>Delivery Method</Form.Label>
                <Form.Select {...register("deliveryMethod")} id="deliveryMethod">
                  <option value="all">All Methods</option>
                  <option value="in-person">In-Person</option>
                  <option value="online">Online</option>
                  <option value="both">Both</option>
                </Form.Select>
              </Col>
              <Col md={6} lg={4}>
                <Form.Label style={{ fontSize: 14, marginBottom: 5 }}>Service Type</Form.Label>
                <Form.Select {...register("serviceType")} id="serviceType">
                  <option value="all">All Types</option>
                  <option value="Legal Support">Legal Support</option>
                  <option value="Training">Training</option>
                  <option value="Transport">Transport</option>
                  <option value="Therapeutic Services">Therapeutic Services</option>
                  <option value="Independent Reviewing">Independent Reviewing</option>
                  <option value="Assessment Services">Assessment Services</option>
                  <option value="Family Support">Family Support</option>
                  <option value="Educational Support">Educational Support</option>
                  <option value="Placement Support">Placement Support</option>
                  <option value="Other">Other</option>
                </Form.Select>
              </Col>
            </Row>
          </div>
        </div>

        {/* GRID LAYOUT */}
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "1fr 1fr",
            gap: "15px",
            overflowY: "auto",
            maxHeight: "calc(100vh - 300px)",
          }}
        >
          {filteredServices.length === 0 ? (
            <div
              className="d-flex flex-column justify-content-center align-items-center"
              style={{ marginTop: 40, marginBottom: 40, gridColumn: "1 / -1" }}
            >
              <p style={{ color: variables.grey }}>No services found matching your criteria.</p>
            </div>
          ) : (
            filteredServices.map((service) => <ServiceCard key={service._id} service={service} />)
          )}
        </div>
      </div>
    </Row>
  );
}
